import React from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { Check } from '@mui/icons-material';
import { Box } from '@mui/material';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { normalizeAgentPosType } from '../../../../hooks/logic/useAgentConfigurationCardLogic';
import authService, { type AgentParams, type Store as StoreType } from '../../../../services/authService';
import type { AgentConfigFormValues } from '../../../../types/features/storeDetails/overview';
import {
    agentConfigSchema,
    DEFAULT_AGENT_CONFIG_FORM_VALUES,
    normalizePosTransferEnabled,
} from '../validators/agentConfigSchema';
import {
    AGENT_CONFIGURATION_AVAILABLE_EXTENSIONS,
    formatLastSync,
    formatSeenTimestamp,
    getDeviceIdFromAgentParams,
} from '../utils/storeDetailsOverviewUtils';
import { isGilbarcoType, isVerifoneType } from '../utils/storeDetailsPosSetupUtils';
import { useStoreDetailsOverviewAgentConfig } from './useStoreDetailsOverviewAgentConfig';

type UseStoreDetailsOverviewAgentParams = {
    onSilentStoreRefresh?: () => Promise<StoreType | null> | StoreType | null | void;
    selectedPosType: string;
    store: StoreType | null;
};

const AGENT_PARAMS_POLL_INTERVAL_MS = 30_000;
const LOG_UPLOAD_POLL_INTERVAL_MS = 30_000;
const LOG_UPLOAD_POLL_TIMEOUT_MS = 5 * 60_000;

type PendingLogRequest = {
    expiresAt: number;
    previousUploadedAt: string | null;
    storeId: string;
};

export const useStoreDetailsOverviewAgent = ({
    onSilentStoreRefresh,
    selectedPosType,
    store,
}: UseStoreDetailsOverviewAgentParams) => {
    const {
        agentConfig,
        agentConfigLoading,
        refreshAgentConfig,
        refreshAgentConfigSilently,
        saveAgentConfig,
    } = useStoreDetailsOverviewAgentConfig(store?.id, Boolean(store?.id));
    const [changingDevice, setChangingDevice] = React.useState<boolean>(false);
    const [agentDeviceId, setAgentDeviceId] = React.useState<string | null>(agentConfig?.device_id ?? null);
    const [agentParamsData, setAgentParamsData] = React.useState<AgentParams | null>(null);
    const [agentParamsLoading, setAgentParamsLoading] = React.useState<boolean>(false);
    const [latestLogUploadedAt, setLatestLogUploadedAt] = React.useState<string | null>(null);
    const [pendingLogRequest, setPendingLogRequest] = React.useState<PendingLogRequest | null>(null);
    const [logsDialogOpen, setLogsDialogOpen] = React.useState<boolean>(false);
    const [saving, setSaving] = React.useState<boolean>(false);
    const [retryingVerifoneLogin, setRetryingVerifoneLogin] = React.useState(false);
    const retryVerifoneLoginRef = React.useRef(false);
    const activeStoreIdRef = React.useRef<string | null>(store?.id ?? null);
    const latestLogRequestIdRef = React.useRef(0);
    activeStoreIdRef.current = store?.id ?? null;

    const {
        control,
        handleSubmit,
        reset,
        setValue,
        formState: { isValid, dirtyFields },
    } = useForm<AgentConfigFormValues>({
        resolver: yupResolver(agentConfigSchema),
        context: { requiresHeartbeat: Boolean(normalizeAgentPosType(selectedPosType || store?.pos_type || '')) },
        mode: 'onChange',
        defaultValues: DEFAULT_AGENT_CONFIG_FORM_VALUES,
    });

    const checkboxIcon = React.useMemo(
        () => (
            <Box
                sx={{
                    width: 20,
                    height: 20,
                    m: '4px',
                    mx: '8px',
                    borderRadius: '6px',
                    border: '1px solid #2C6CA0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            />
        ),
        [],
    );

    const checkboxCheckedIcon = React.useMemo(
        () => (
            <Box
                sx={{
                    width: 20,
                    height: 20,
                    m: '4px',
                    mx: '8px',
                    borderRadius: '6px',
                    border: '1px solid #2C6CA0',
                    backgroundColor: '#2C6CA0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                <Check sx={{ fontSize: 16, color: '#FFFFFF' }} />
            </Box>
        ),
        [],
    );

    React.useEffect(() => {
        if (!agentConfig?.config) return;

        const config = agentConfig.config;
        const networkShare = config.NetworkShare ?? config.GilbarcoPassport?.NetworkShare ?? null;
        const readPath = config.posConfig?.ReadPath ?? '';
        const writePath = config.posConfig?.WritePath ?? '';
        reset({
            Heartbeat: {
                IntervalMinutes: config.Heartbeat?.IntervalMinutes ?? DEFAULT_AGENT_CONFIG_FORM_VALUES.Heartbeat.IntervalMinutes,
            },
            ScannerProxy: {
                Enabled: config.ScannerProxy?.Enabled ?? true,
                AutoDetect: config.ScannerProxy?.AutoDetect ?? true,
                Port: config.ScannerProxy?.Port ?? '',
                BaudRate: typeof config.ScannerProxy?.BaudRate === 'number' ? config.ScannerProxy.BaudRate : null,
            },
            PosDataUpload: {
                Enabled: normalizePosTransferEnabled(config.PosDataUpload?.Enabled, readPath),
            },
            PosDataDownload: {
                Enabled: normalizePosTransferEnabled(config.PosDataDownload?.Enabled, writePath),
            },
            posConfig: {
                ReadPath: readPath,
                WritePath: writePath,
                IncludeSubdirectories: config.posConfig?.IncludeSubdirectories ?? true,
                EligibleExtensions: config.posConfig?.EligibleExtensions ?? [],
            },
            NetworkShare: {
                Root: networkShare?.Root ?? '',
                User: networkShare?.User ?? '',
                Password: networkShare?.Password ?? '',
            },
            Verifone: {
                BaseUrl: config.Verifone?.BaseUrl ?? '',
                User: config.Verifone?.User ?? '',
                Password: config.Verifone?.Password ?? '',
                UseCompressed: config.Verifone?.UseCompressed ?? false,
                AllowInsecure: config.Verifone?.AllowInsecure ?? true,
                NoProxy: config.Verifone?.NoProxy ?? true,
                TimeoutSeconds: config.Verifone?.TimeoutSeconds ?? 60,
            },
        }, { keepDirtyValues: true });
    }, [agentConfig, reset]);

    React.useEffect(() => {
        const deviceId = typeof agentConfig?.device_id === 'string' ? agentConfig.device_id.trim() : '';
        setAgentDeviceId(deviceId || null);
    }, [agentConfig?.device_id]);

    const loadAgentParams = React.useCallback(async (showLoader = true) => {
        if (!store?.id) {
            if (showLoader) {
                setAgentParamsLoading(false);
            }
            return;
        }

        if (showLoader) {
            setAgentParamsLoading(true);
        }

        try {
            const params = await authService.getAgentParams(store.id);
            setAgentDeviceId(getDeviceIdFromAgentParams(params));
            setAgentParamsData(params);
        } catch (error) {
            console.error('Failed to load agent params', error);
        } finally {
            if (showLoader) {
                setAgentParamsLoading(false);
            }
        }
    }, [store?.id]);

    const loadLatestLogUploadedAt = React.useCallback(async (): Promise<string | null | undefined> => {
        const requestedStoreId = store?.id;
        if (!requestedStoreId) {
            setLatestLogUploadedAt(null);
            return null;
        }

        const requestId = ++latestLogRequestIdRef.current;
        try {
            const logs = await authService.getStoreLogs(requestedStoreId, 1, 0);
            const uploadedAt = logs.files[0]?.uploaded_at ?? null;
            if (
                activeStoreIdRef.current !== requestedStoreId ||
                latestLogRequestIdRef.current !== requestId
            ) {
                return undefined;
            }
            setLatestLogUploadedAt(uploadedAt);
            return uploadedAt;
        } catch (error) {
            console.error('Failed to load latest agent log timestamp', error);
            return undefined;
        }
    }, [store?.id]);

    React.useEffect(() => {
        latestLogRequestIdRef.current += 1;
        setLatestLogUploadedAt(null);
        setPendingLogRequest(null);
    }, [store?.id]);

    React.useEffect(() => {
        void loadAgentParams();
        void loadLatestLogUploadedAt();

        const intervalId = window.setInterval(() => {
            void loadAgentParams(false);
        }, AGENT_PARAMS_POLL_INTERVAL_MS);

        return () => {
            window.clearInterval(intervalId);
        };
    }, [loadAgentParams, loadLatestLogUploadedAt]);

    React.useEffect(() => {
        if (!pendingLogRequest || pendingLogRequest.storeId !== store?.id) return;

        let cancelled = false;
        const pollForUploadedLog = async () => {
            if (Date.now() >= pendingLogRequest.expiresAt) {
                setPendingLogRequest((current) => current === pendingLogRequest ? null : current);
                return;
            }

            const uploadedAt = await loadLatestLogUploadedAt();
            if (
                !cancelled &&
                uploadedAt !== undefined &&
                uploadedAt !== null &&
                uploadedAt !== pendingLogRequest.previousUploadedAt
            ) {
                setPendingLogRequest((current) => current === pendingLogRequest ? null : current);
            }
        };

        const intervalId = window.setInterval(() => {
            void pollForUploadedLog();
        }, LOG_UPLOAD_POLL_INTERVAL_MS);

        return () => {
            cancelled = true;
            window.clearInterval(intervalId);
        };
    }, [loadLatestLogUploadedAt, pendingLogRequest, store?.id]);

    const agentLastSeenLabel = React.useMemo(
        () =>
            formatSeenTimestamp(agentParamsData?.timestamp) ||
            formatLastSync(agentParamsData?.created_at, agentParamsData?.updated_at) ||
            '—',
        [agentParamsData?.created_at, agentParamsData?.timestamp, agentParamsData?.updated_at],
    );

    const agentLastFetchedLabel = React.useMemo(
        () => formatSeenTimestamp(latestLogUploadedAt) || '—',
        [latestLogUploadedAt],
    );

    const handleSaveAgentConfig = React.useCallback(async (values: AgentConfigFormValues) => {
        if (!store?.id) {
            toast.error('Store ID is missing');
            return;
        }

        const nextPosType = selectedPosType.trim() || (store.pos_type || '').trim();
        if (!nextPosType) {
            toast.error('POS type is required');
            return;
        }

        setSaving(true);
        try {
            if ((store.pos_type || '').trim() !== nextPosType) {
                await authService.updateStore(store.id, { pos_type: nextPosType });
                await Promise.resolve(onSilentStoreRefresh?.());
            }

            const verifoneSelected = isVerifoneType(nextPosType);
            const gilbarcoSelected = isGilbarcoType(nextPosType);
            const requiresAgentConfig = verifoneSelected || gilbarcoSelected;

            if (!requiresAgentConfig) {
                toast.success('POS type saved successfully');
                return;
            }

            const payload: Record<string, unknown> = {
                ScannerProxy: {
                    Enabled: values.ScannerProxy?.Enabled ?? true,
                    AutoDetect: values.ScannerProxy?.AutoDetect ?? true,
                    Port: values.ScannerProxy?.Port ?? '',
                    BaudRate: values.ScannerProxy?.BaudRate ?? null,
                },
            };
            if (normalizeAgentPosType(nextPosType)) {
                payload.Heartbeat = { IntervalMinutes: values.Heartbeat.IntervalMinutes };
            }
            let savedValues = values;

            if (verifoneSelected) {
                payload.Verifone = {
                    BaseUrl: values.Verifone?.BaseUrl ?? '',
                    User: values.Verifone?.User ?? '',
                    Password: values.Verifone?.Password ?? '',
                    UseCompressed: values.Verifone?.UseCompressed ?? false,
                    AllowInsecure: values.Verifone?.AllowInsecure ?? true,
                    NoProxy: values.Verifone?.NoProxy ?? true,
                    TimeoutSeconds: values.Verifone?.TimeoutSeconds ?? 60,
                };
            } else {
                const uploadEnabled = normalizePosTransferEnabled(
                    values.PosDataUpload?.Enabled,
                    values.posConfig?.ReadPath,
                );
                const downloadEnabled = normalizePosTransferEnabled(
                    values.PosDataDownload?.Enabled,
                    values.posConfig?.WritePath,
                );
                const networkShare = {
                    Root: values.NetworkShare?.Root ?? '',
                    User: values.NetworkShare?.User ?? '',
                    Password: values.NetworkShare?.Password ?? '',
                };
                payload.PosDataUpload = {
                    Enabled: uploadEnabled,
                };
                payload.PosDataDownload = {
                    Enabled: downloadEnabled,
                };
                payload.posConfig = values.posConfig;
                payload.NetworkShare = networkShare;
                savedValues = {
                    ...values,
                    PosDataUpload: { Enabled: uploadEnabled },
                    PosDataDownload: { Enabled: downloadEnabled },
                };
            }

            await saveAgentConfig(payload);
            reset(savedValues);
            await refreshAgentConfigSilently();
            toast.success('Agent configuration saved successfully');
        } catch (error: any) {
            console.error('Failed to save agent config', error);
            toast.error(error?.detail || 'Failed to save agent configuration. Please try again.');
        } finally {
            setSaving(false);
        }
    }, [onSilentStoreRefresh, refreshAgentConfigSilently, reset, saveAgentConfig, selectedPosType, store]);

    const handleDownloadLogs = React.useCallback(() => {
        if (!store?.id) {
            toast.error('Store ID is missing');
            return;
        }
        setLogsDialogOpen(true);
    }, [store?.id]);

    const handleFetchLatestLogs = React.useCallback(async () => {
        if (!store?.id) {
            toast.error('Store ID is missing');
            throw new Error('missing_store_id');
        }

        try {
            const requestedStoreId = store.id;
            const previousUploadedAt = await loadLatestLogUploadedAt();
            const utcTimestamp = new Date().toISOString().replace(/\.\d{3}Z$/, 'Z');
            await authService.uploadAgentLogs(requestedStoreId, {
                upload_logs: true,
                upload_logs_timestamp: utcTimestamp,
            });
            if (activeStoreIdRef.current === requestedStoreId) {
                setPendingLogRequest({
                    expiresAt: Date.now() + LOG_UPLOAD_POLL_TIMEOUT_MS,
                    previousUploadedAt: previousUploadedAt === undefined
                        ? latestLogUploadedAt
                        : previousUploadedAt,
                    storeId: requestedStoreId,
                });
                await loadAgentParams(false);
            }
        } catch (error) {
            console.error('Failed to fetch latest logs', error);
            toast.error('Failed to fetch latest logs. Please try again.');
            throw error;
        }
    }, [latestLogUploadedAt, loadAgentParams, loadLatestLogUploadedAt, store?.id]);

    const handleChangeDevice = React.useCallback(async () => {
        if (!store?.id) {
            toast.error('Store ID is missing');
            return;
        }

        if (changingDevice) return;

        setChangingDevice(true);
        try {
            const updatedParams = await authService.updateAgentParams(store.id, { device_id: null });
            setAgentDeviceId(getDeviceIdFromAgentParams(updatedParams));
            await refreshAgentConfig();
            toast.success('Device reset successfully');
        } catch (error) {
            console.error('Failed to change device', error);
            toast.error('Failed to change device. Please try again.');
        } finally {
            setChangingDevice(false);
        }
    }, [changingDevice, refreshAgentConfig, store?.id]);

    const hasAgentDeviceId = Boolean(agentDeviceId && agentDeviceId.trim());
    const isAgentLoading = agentConfigLoading || agentParamsLoading;
    const verifoneLoginRetryNeedsSave = Boolean(
        !agentConfig?.config?.Verifone?.User?.trim() ||
        !agentConfig?.config?.Verifone?.Password?.trim() ||
        dirtyFields.Verifone || normalizeAgentPosType(store?.pos_type || '') !== 'verifone',
    );
    const canRetryVerifoneLogin = Boolean(
        store?.id && agentConfig && !isAgentLoading && !saving && !changingDevice && !retryingVerifoneLogin &&
        !verifoneLoginRetryNeedsSave && normalizeAgentPosType(selectedPosType || store.pos_type || '') === 'verifone',
    );

    const handleRetryVerifoneLogin = React.useCallback(async () => {
        if (!store?.id || !canRetryVerifoneLogin || retryVerifoneLoginRef.current) return;
        retryVerifoneLoginRef.current = true;
        setRetryingVerifoneLogin(true);
        try {
            await authService.updateAgentParams(store.id, { retry_verifone_login: true });
            toast.success('Verifone login retry requested');
        } catch {
            toast.error('Failed to request Verifone login retry. Please try again.');
        } finally {
            retryVerifoneLoginRef.current = false;
            setRetryingVerifoneLogin(false);
        }
    }, [canRetryVerifoneLogin, store?.id]);

    return {
        agentConfig,
        agentConfigForm: {
            control,
            handleSubmit,
            isValid,
            setValue,
        },
        agentDeviceId,
        agentLastFetchedLabel,
        agentLastSeenAt: agentParamsData?.timestamp ?? null,
        agentLastSeenLabel,
        availableExtensions: AGENT_CONFIGURATION_AVAILABLE_EXTENSIONS,
        changingDevice,
        canRetryVerifoneLogin,
        handleRetryVerifoneLogin,
        retryingVerifoneLogin,
        verifoneLoginRetryNeedsSave,
        checkboxCheckedIcon,
        checkboxIcon,
        handleChangeDevice,
        handleDownloadLogs,
        handleFetchLatestLogs,
        handleSaveAgentConfig,
        hasAgentDeviceId,
        isAgentLoading,
        logsDialogOpen,
        refreshAgentConfig,
        saving,
        setLogsDialogOpen,
        showAgentNotFound: !isAgentLoading && !hasAgentDeviceId,
        showLastSeen: Boolean(agentConfig && !agentConfigLoading && agentParamsData),
    };
};
